import { useState, useEffect } from 'react'

type CameraState =
  | {
      state: 'available'
      mediaStream: MediaStream
    }
  | {
      state: 'unavailable'
    }
  | {
      state: 'unknown'
    }
  | {
      state: 'denied'
    }

export const useCamera = (constraints: MediaStreamConstraints): CameraState => {
  const [cameraState, setCameraState] = useState<CameraState>({ state: 'unknown' })

  useEffect(() => {
    let isMounted = true
    let currentMediaStream: MediaStream | null = null

    const isCameraAvailable = () =>
      new Promise((resolve) => {
        if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
          navigator.mediaDevices
            .enumerateDevices()
            .then((devices) => {
              resolve(devices.some((device) => device.kind === 'videoinput'))
            })
            .catch(() => {
              resolve(false)
            })
        } else {
          resolve(false)
        }
      })

    const isCameraAccessDenied = () =>
      new Promise((resolve) => {
        if (navigator.permissions) {
          navigator.permissions
            .query({ name: 'camera' as PermissionName })
            .then((status) => {
              resolve(status.state === 'denied')
            })
            .catch(() => {
              // Unable to determine, assume camera is available
              resolve(false)
            })
        } else {
          // Unable to determine, assume camera is available
          resolve(false)
        }
      })

    Promise.all([isCameraAvailable(), isCameraAccessDenied()]).then(([isAvailable, isDenied]) => {
      if (isMounted) {
        if (!isAvailable) {
          setCameraState({ state: 'unavailable' })
          return
        }

        if (isDenied) {
          setCameraState({ state: 'denied' })
          return
        }

        navigator.mediaDevices
          .getUserMedia(constraints)
          .then((mediaStream) => {
            if (isMounted) {
              currentMediaStream = mediaStream

              setCameraState({ state: 'available', mediaStream })
            }
          })
          .catch((e) => {
            if (isMounted) {
              switch (e.name) {
                case 'NotAllowedError':
                  setCameraState({ state: 'denied' })
                  break
                default:
                  setCameraState({ state: 'unavailable' })
              }
            }
          })
      }
    })

    return () => {
      isMounted = false

      if (currentMediaStream) {
        currentMediaStream.getTracks().forEach((track) => {
          track.stop()
        })

        currentMediaStream = null
      }
    }
  }, [constraints])

  return cameraState
}
